import React, { type ComponentType, useCallback, type Ref, useMemo, useRef } from 'react';
import type { TerminalClause } from '@atlaskit/jql-ast';
import { useIntl } from '@atlassian/jira-intl';
import type { AdditionalEventAttributes } from '@atlassian/jira-common-experience-tracking-analytics/src/index.tsx';
import type {
	SearchTemplate,
	KeyboardEventType,
	EventType,
	AllowedOperators,
	OnChangeOperator,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import type { PickerProps } from '@atlassian/jira-jql-builder-basic-picker/src/ui/types.tsx';
import useMergeRefs from '@atlassian/jira-merge-refs/src/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import PickerSync from '@atlassian/jira-jql-builder-basic-picker/src/index.tsx';
import PickerFallback from '../../../../../common/ui/picker-fallback/index.tsx';
import PickerTrigger, {
	type PickerTriggerProps,
} from '../../../../../common/ui/picker-trigger/main.tsx';
import { TriggerComponentNoOperator } from '../../../../../common/ui/picker-trigger/trigger-component-no-operator/main.tsx';
import TriggerComponent from '../../../../../common/ui/picker-trigger/trigger-component/index.tsx';
import messages from './messages.tsx';

// @deprecated render_jql_builder_in_ssr will load JQL Builder Picker sync
// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const PickerAsync = lazyForPaint<ComponentType<PickerProps>>(
	() =>
		import(
			/* webpackChunkName: "async-jql-builder-basic-picker" */ '@atlassian/jira-jql-builder-basic-picker'
		),
	{ ssr: false },
);

const isKeyboardEvent = (event: EventType): event is KeyboardEventType =>
	Object.hasOwn(event, 'key');

export type PickerLoaderProps = {
	isOpen: PickerProps['isOpen'];
	isLoading: boolean;
	isInvalid: boolean;
	isEmpty: boolean;
	value: PickerProps['value'];
	clause: TerminalClause | undefined;
	fieldId: string;
	fieldDisplayName: string | undefined | null;
	fieldInvalidLabel?: string;
	enableFullList?: boolean;
	operandDisplayValues?: string[];
	showOperandDisplayValuesInFullList?: boolean;
	onClearValues: PickerProps['onClearValues'];
	onClose: PickerProps['onClose'];
	onOpen: () => void;
	onChange: PickerProps['onChange'];
	onLoadOptions: PickerProps['onLoadOptions'];
	onClearPicker: () => void;
	onChangeOperator: OnChangeOperator;
	allowedOperators: AllowedOperators;
	selectedOperator: PickerProps['selectedOperator'];
	isMulti?: boolean;
	searchTemplate: SearchTemplate;
	fieldDescription: string | null;
	onOpenOperatorPicker: () => void;
	hideOperatorDropdown?: boolean;
	isIrremovable?: boolean;
};

type TriggerWrapperProps = PickerTriggerProps & {
	triggerRef: Ref<HTMLDivElement>;
	popupRef: Ref<HTMLElement>;
};

const TriggerButtonWrapper = ({
	children,
	popupRef,
	triggerRef,
	...props
}: TriggerWrapperProps) => {
	const ref = useMergeRefs(popupRef, triggerRef);

	return (
		<PickerTrigger ref={ref} {...props}>
			{children}
		</PickerTrigger>
	);
};

export const PickerLoader = ({
	clause,
	fieldId,
	fieldDisplayName,
	isLoading,
	isOpen,
	onOpen,
	onClose,
	fieldInvalidLabel,
	onChange,
	onClearValues,
	onLoadOptions,
	enableFullList,
	operandDisplayValues,
	showOperandDisplayValuesInFullList,
	isInvalid,
	isEmpty,
	onClearPicker,
	value,
	onChangeOperator,
	allowedOperators,
	selectedOperator,
	isMulti,
	searchTemplate,
	fieldDescription,
	onOpenOperatorPicker,
	hideOperatorDropdown,
	isIrremovable,
}: PickerLoaderProps) => {
	const buttonRef = useRef<HTMLDivElement>(null);
	const { formatMessage } = useIntl();

	const onTrigger = useCallback(
		({ ref }: { ref: Ref<HTMLElement> }) => (
			<TriggerButtonWrapper
				isInvalid={isInvalid}
				isSelected={isOpen}
				hasValues={!isEmpty}
				onClick={(event) => {
					isOpen ? onClose(event) : onOpen();
				}}
				onClear={onClearPicker}
				popupRef={ref}
				triggerRef={buttonRef}
				testId={`jql-builder-basic.ui.jql-editor.picker.filter-button.${fieldId}`}
				fieldDescription={fieldDescription}
				/**
				 * We need to show loading state when:
				 * 1) clause is defined (values are selected) and
				 * 2) we are waiting for hydrtation to finish (isLoading === true)
				 * 3) Picker is closed
				 * 4) There are no hydrated values (new or old) for current clause
				 */
				isLoading={clause && isLoading && !isOpen && value.length === 0}
				searchTemplate={searchTemplate}
				isIrremovable={isIrremovable}
			>
				{!hideOperatorDropdown ? (
					<TriggerComponent
						label={fieldDisplayName}
						value={value}
						isInvalid={isInvalid}
						onChangeOperator={onChangeOperator}
						selectedOperator={selectedOperator}
						allowedOperators={allowedOperators}
						isParentPickerOpen={isOpen}
						onOpenOperatorPicker={onOpenOperatorPicker}
						{...(isVisualRefreshEnabled() &&
							fg('visual-refresh-headers-buttons') && {
								isSelected: isOpen,
							})}
					/>
				) : (
					<TriggerComponentNoOperator
						label={fieldDisplayName}
						value={value}
						isInvalid={isInvalid}
						{...(isVisualRefreshEnabled() &&
							fg('visual-refresh-headers-buttons') && {
								isSelected: isOpen,
							})}
					/>
				)}
			</TriggerButtonWrapper>
		),
		[
			isInvalid,
			isOpen,
			isEmpty,
			fieldId,
			onClearPicker,
			fieldDescription,
			clause,
			isLoading,
			value,
			searchTemplate,
			isIrremovable,
			hideOperatorDropdown,
			allowedOperators,
			onOpenOperatorPicker,
			fieldDisplayName,
			onChangeOperator,
			selectedOperator,
			onClose,
			onOpen,
		],
	);

	const experienceAnalyticsAttributes = useMemo<AdditionalEventAttributes | {}>(
		() => (searchTemplate ? { searchTemplate } : {}),
		[searchTemplate],
	);

	const loadingMessage = useMemo(() => {
		return fieldDisplayName && fg('jql_basic_picker_infinite_scroll_loading_fix')
			? {
					paginationStart: formatMessage(messages.loadingMessageStart, {
						fieldName: fieldDisplayName,
					}),
					paginationEnd: formatMessage(messages.loadingMessageEnd, { fieldName: fieldDisplayName }),
				}
			: undefined;
	}, [formatMessage, fieldDisplayName]);

	const handleClose = useCallback(
		(
			event:
				| Event
				| React.MouseEvent<HTMLElement>
				| KeyboardEvent
				| React.KeyboardEvent<HTMLDivElement>,
		) => {
			onClose(event);
			if (isKeyboardEvent(event) && event.key === 'Escape') {
				buttonRef.current?.focus();
			}
		},
		[onClose],
	);

	return (
		<>
			{fg('render_jql_builder_in_ssr') ? (
				<PickerSync
					isOpen={isOpen}
					onClose={handleClose}
					value={value}
					id={fieldId}
					searchInputPlaceholder={formatMessage(messages.searchPlaceholder, {
						fieldName: fieldDisplayName,
					})}
					invalidLabel={fieldInvalidLabel}
					onChange={onChange}
					onClearValues={onClearValues}
					onLoadOptions={onLoadOptions}
					enableFullList={enableFullList}
					operandDisplayValues={operandDisplayValues}
					showOperandDisplayValuesInFullList={showOperandDisplayValuesInFullList}
					onTrigger={onTrigger}
					shouldRenderToParent
					showOperatorSelect={!hideOperatorDropdown}
					onChangeOperator={onChangeOperator}
					allowedOperators={allowedOperators}
					selectedOperator={selectedOperator}
					isMulti={isMulti}
					experienceAnalyticsAttributes={experienceAnalyticsAttributes}
					loadingMessages={loadingMessage}
				/>
			) : (
				<Placeholder
					name="picker-loader-picker"
					fallback={
						<PickerFallback
							clause={clause}
							fieldId={fieldId}
							fieldDisplayName={fieldDisplayName}
							fieldDescription={fieldDescription}
							isIrremovable={isIrremovable}
						/>
					}
				>
					<PickerAsync
						isOpen={isOpen}
						onClose={handleClose}
						value={value}
						id={fieldId}
						searchInputPlaceholder={formatMessage(messages.searchPlaceholder, {
							fieldName: fieldDisplayName,
						})}
						invalidLabel={fieldInvalidLabel}
						onChange={onChange}
						onClearValues={onClearValues}
						onLoadOptions={onLoadOptions}
						enableFullList={enableFullList}
						operandDisplayValues={operandDisplayValues}
						showOperandDisplayValuesInFullList={showOperandDisplayValuesInFullList}
						onTrigger={onTrigger}
						shouldRenderToParent
						showOperatorSelect={!hideOperatorDropdown}
						onChangeOperator={onChangeOperator}
						allowedOperators={allowedOperators}
						selectedOperator={selectedOperator}
						isMulti={isMulti}
						experienceAnalyticsAttributes={experienceAnalyticsAttributes}
						loadingMessages={loadingMessage}
					/>
				</Placeholder>
			)}
		</>
	);
};

export default PickerLoader;
